import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApolloQueryResult, useQuery } from '@apollo/client';
import { default as Form } from '../../components/connectedForm';
import EdataProduct from '../../graphql/entities/product/EdataProduct';

// Quick filter queries
import EdataManufacturerNames from '../../graphql/queries/product/EdataManufacturerNamesQuery.graphql';
import EdataFamilies from '../../graphql/queries/product/EdataFamiliesQuery.graphql';
import EdataRanges from '../../graphql/queries/product/EdataRangesQuery.graphql';

import { useHistory, useLocation } from 'react-router';
import {
  FilterOperation,
  IFilterInput,
  IEdataManufacturerNamesQueryVariables,
  IEdataManufacturerNamesQueryResult,
  IEdataFamiliesQueryVariables,
  IEdataFamiliesQueryResult,
  IEdataRangesQueryVariables,
  IEdataRangesQueryResult,
  IStringEdge,
} from '../../graphql/schema/p4types';
import { QUERY_PAGE_SIZE } from '../../components/FluentWrapper/useQueryForDetailsList';
import { FluentTable, IFluentTableRowSize } from '../../components/view/FluentViewTable';
import { TableColumnDefinition, UseTableFeaturesOptions } from '@fluentui/react-table';
import { createTableColumn, TableRowId } from '@fluentui/react-components';
import EdataProductLightWorkspace from './EdataProductLightWorkspace';
import Label from '../../components/connectedForm/Label';
import { useFullUserDetails } from '../../utils/hooks';
import { HoldingMessage } from '../../components/FeatureFlag/HoldingMessage';
import { EdataHoldingInfo } from '../../components/FeatureFlag/EdataHoldingInfo';

const EdataEnquiryFluentTable = ({
  fetchMore,
  itemCount,
  items,
  columns,
  setRemoteFilters,
  setSelectedItems,
}: {
  fetchMore?: Function;
  itemCount: number;
  items: Array<any>;
  columns: Array<TableColumnDefinition<any>>;
  setRemoteFilters: (filters: Array<IFilterInput>) => void;
  setSelectedItems: (selectedItems: Array<any>) => void;
}) => {
  const tableFeatureOptions = useMemo<UseTableFeaturesOptions<any>>(
    () => ({
      columns: columns,
      items: items,
    }),
    [columns, items]
  );

  //Local state for selected options - By id
  const [localSelectedOptionsIds, setLocalSelectedOptionsIds] = useState<Set<TableRowId>>(new Set());

  const loadedPagesRef = useRef<{ [key: number]: boolean }>({ 0: true });
  const isItemLoaded = useCallback((index: number) => {
    return loadedPagesRef.current[(index / QUERY_PAGE_SIZE) >> 0];
  }, []);

  const loadMoreItems: (startIndex: number, stopIndex: number) => Promise<void> | void = (startIndex, stopIndex) => {
    const startPage = (startIndex / QUERY_PAGE_SIZE) >> 0;
    const stopPage = (stopIndex / QUERY_PAGE_SIZE) >> 0;
    const promises: Array<Promise<ApolloQueryResult<any>>> = [];
    for (let i = startPage; i <= stopPage; i++) {
      if (!loadedPagesRef.current[i]) {
        promises.push(
          fetchMore?.({
            variables: {
              first: QUERY_PAGE_SIZE,
              after: i * QUERY_PAGE_SIZE,
            },
          })
        );
        loadedPagesRef.current[i] = true;
      }
    }
    return Promise.all(promises) as unknown as Promise<void>;
  };

  //Maintain a list of all items that we've loaded this session - If
  const [allItemsById, setAllItemsById] = useState<{ [key: TableRowId]: any }>({});
  useEffect(() => {
    setAllItemsById((prev) => {
      const newObj = { ...prev };
      items?.forEach((value) => {
        if (value) {
          newObj[value.id] = value;
        }
      });
      return newObj;
    });
  }, [items]);

  return (
    <FluentTable
      itemCount={itemCount}
      height={246}
      tableFeatureOptions={tableFeatureOptions}
      selectionMode={'multiselect'}
      selectionKey={'id'}
      onSelectionChange={(ev, data) => {
        const selectedItems: Array<any> = [];
        data.selectedItems.forEach((tableRowId) => {
          const selectedItem = allItemsById[tableRowId];
          if (selectedItem) {
            selectedItems.push(selectedItem);
          }
        });

        setLocalSelectedOptionsIds(data.selectedItems);
        setSelectedItems(selectedItems);
      }}
      selectedItems={localSelectedOptionsIds}
      size={IFluentTableRowSize.ExtraSmall}
      infiniteScrollConfig={
        fetchMore
          ? {
              isItemLoaded,
              loadMoreItems,
              setRemoteFilters: (newFilters) => {
                console.log('new filters: ', newFilters);
                //Reset loaded pages ref when filters change - Loaded set no longer valid.
                loadedPagesRef.current = { 0: true };
                setRemoteFilters(newFilters);
              },
            }
          : undefined
      }
    />
  );
};

//These are actually strings, but we represent them as objects for table display - This is just a wrapper type.
type IRange = { id: string; value: string };

const ProductRangeFilter = ({ setWorkspaceFilters }: any) => {
  const [selectedManufacturers, setSelectedManufacturers] = useState<Array<{ id: string; value: string }>>([]);
  const [selectedFamilies, setSelectedFamilies] = useState<Array<{ id: string; value: string }>>([]);
  const [selectedRanges, setSelectedRanges] = useState<Array<{ id: string; value: string }>>([]);

  useEffect(() => {
    const manufacturerFilters: Array<IFilterInput> = selectedManufacturers.map((value: any) => {
      return {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'manufacturerName',
          value: value.value,
        },
      };
    });
    const familyFilters: Array<IFilterInput> = selectedFamilies.map((value: any) => {
      return {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'family',
          value: value.value,
        },
      };
    });
    const rangeFilters: Array<IFilterInput> = selectedRanges.map((value: any) => {
      return {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'range',
          value: value.value,
        },
      };
    });

    setWorkspaceFilters([...manufacturerFilters, ...familyFilters, ...rangeFilters]);
  }, [selectedManufacturers, selectedFamilies, selectedRanges, setWorkspaceFilters]);

  const [manufacturerPrefilters, setManufacturerPrefilters] = useState<Array<IFilterInput>>([]);
  const [familyPrefilters, setFamilyPrefilters] = useState<Array<IFilterInput>>([]);
  const [rangePrefilters, setRangePrefilters] = useState<Array<IFilterInput>>([]);

  const familyFilters: Array<IFilterInput> = useMemo(() => {
    return [
      ...familyPrefilters,
      ...selectedManufacturers.map((value) => {
        return {
          filterOperation: FilterOperation.In,
          filterParameters: {
            field: 'manufacturerName',
            value: value.value,
          },
        };
      }),
    ];
  }, [familyPrefilters, selectedManufacturers]);
  const rangeFilters: Array<IFilterInput> = useMemo(() => {
    return [
      ...familyFilters,
      ...rangePrefilters,
      ...selectedFamilies.map((value) => {
        return {
          filterOperation: FilterOperation.In,
          filterParameters: {
            field: 'family',
            value: value.value,
          },
        };
      }),
    ];
  }, [familyFilters, rangePrefilters, selectedFamilies]);
  const { data: manufacturersData, fetchMore: fetchMoreForManufacturers } = useQuery<
    IEdataManufacturerNamesQueryResult,
    IEdataManufacturerNamesQueryVariables
  >(EdataManufacturerNames, {
    variables: { filters: manufacturerPrefilters },
    onCompleted: () => setSelectedRanges([]),
  });
  const { data: familiesData, fetchMore: fetchMoreForFamilies } = useQuery<
    IEdataFamiliesQueryResult,
    IEdataFamiliesQueryVariables
  >(EdataFamilies, {
    variables: { filters: familyFilters },
    onCompleted: () => setSelectedRanges([]),
  });
  const { data: rangesData, fetchMore: fetchMoreForRanges } = useQuery<
    IEdataRangesQueryResult,
    IEdataRangesQueryVariables
  >(EdataRanges, {
    variables: { filters: rangeFilters },
  });

  const manufacturers = (manufacturersData?.list?.edges || []).map((edge) => ({
    id: (edge as IStringEdge).node,
    value: (edge as IStringEdge).node,
  }));
  const families = (familiesData?.list?.edges || []).map((edge) => ({
    id: (edge as IStringEdge).node,
    value: (edge as IStringEdge).node,
  }));
  const ranges = (rangesData?.list?.edges || []).map((edge) => ({
    id: (edge as IStringEdge).node,
    value: (edge as IStringEdge).node,
  }));

  const manufacturerColumns = useMemo(() => {
    const renderCell = (item: IRange) => item?.value;
    return [
      createTableColumn<IRange>({
        columnId: 'manufacturerName',
        renderCell: renderCell,
      }),
    ];
  }, []);

  const familyColumns = useMemo(() => {
    const renderCell = (item: IRange) => item?.value;
    return [
      createTableColumn<IRange>({
        columnId: 'family',
        renderCell: renderCell,
      }),
    ];
  }, []);
  const rangeColumns = useMemo(() => {
    const renderCell = (item: IRange) => item?.value;
    return [
      createTableColumn<IRange>({
        columnId: 'range',
        renderCell: renderCell,
      }),
    ];
  }, []);

  return (
    <Form.Row style={{ marginLeft: 0, marginRight: 0 }}>
      <Form.Fieldset cols={1 / 3}>
        <Label>Manufacturer</Label>
        <div style={{ height: 300, position: 'relative' }}>
          <EdataEnquiryFluentTable
            fetchMore={fetchMoreForManufacturers}
            items={manufacturers}
            columns={manufacturerColumns}
            itemCount={manufacturersData?.list?.count ?? 0}
            setRemoteFilters={setManufacturerPrefilters}
            setSelectedItems={setSelectedManufacturers}
          />
        </div>
      </Form.Fieldset>
      <Form.Fieldset cols={1 / 3}>
        <Label>Family</Label>
        <div style={{ height: 300, position: 'relative' }}>
          <EdataEnquiryFluentTable
            fetchMore={fetchMoreForFamilies}
            items={families}
            columns={familyColumns}
            itemCount={familiesData?.list?.count ?? 0}
            setRemoteFilters={(filters: Array<IFilterInput>) =>
              setFamilyPrefilters(
                filters.map((filter: IFilterInput) => ({
                  ...filter,
                  filterParameters: { field: 'family', value: filter.filterParameters?.value },
                }))
              )
            }
            setSelectedItems={setSelectedFamilies}
          />
        </div>
      </Form.Fieldset>
      <Form.Fieldset cols={1 / 3}>
        <Label>Range</Label>
        <div style={{ height: 300, position: 'relative' }}>
          <EdataEnquiryFluentTable
            fetchMore={fetchMoreForRanges}
            items={ranges}
            columns={rangeColumns}
            itemCount={rangesData?.list?.count ?? 0}
            setRemoteFilters={(filters: Array<IFilterInput>) =>
              setRangePrefilters(
                filters.map((filter: IFilterInput) => ({
                  ...filter,
                  filterParameters: { field: 'range', value: filter.filterParameters?.value },
                }))
              )
            }
            setSelectedItems={setSelectedRanges}
          />
        </div>
      </Form.Fieldset>
    </Form.Row>
  );
};

const EdataEnquiryView = (props: any) => {
  const history = useHistory();
  const [workspaceFilters, setWorkspaceFilters] = useState([]);

  const userDetails = useFullUserDetails();

  if (!userDetails?.flags?.edataEnabled) {
    return <HoldingMessage {...EdataHoldingInfo} />;
  }

  const onRowClick = (rowData: any, rowIdx: number) => {
    if (props.onRowClick) {
      props.onRowClick(rowData, rowIdx);
      return;
    }
    window.open(EdataProduct.formUrl + rowData.edataProductId, '_blank');
  };

  const enquiryHeader = (
    <>
      {props.embeddedView ? null : (
        <Form.HeaderBlock name={'Edata Enquiry'} onCancel={history.goBack} noDeleteOption noSaveOption />
      )}
      <ProductRangeFilter setWorkspaceFilters={setWorkspaceFilters} />
    </>
  );

  return (
    <EdataProductLightWorkspace
      {...props}
      embeddedView={true}
      predefinedFilters={workspaceFilters}
      viewScreenName={props.viewScreenName}
      entity={EdataProduct}
      onRowClick={onRowClick}
      noForm={true}
      contentHeight={Math.max(450, window.innerHeight - 528)}
      customHeader={enquiryHeader}
      ignoreContainerWidth={true}
      {...props.workspaceProps}
    />
  );
};

export default EdataEnquiryView;
